// src/pages/servicos/tecnicos/TecnicoResponsavel.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/input/Input';
import { useFilial } from '../../../contexts/administrativo/FilialContext';
import { useOrdemServico } from '../../../contexts/servicos/OrdemServicoContext';
import { useTecnicoResponsavel } from '../../../contexts/servicos/TecnicoResponsavelContext';
import { dataHora, moeda, SITUACOES_OS } from '../../../models/servicos/OrdemServico';
import '../ordensServico/Servicos.css';

const dataLocal = data => {
  const deslocamento = data.getTimezoneOffset() * 60000;
  return new Date(data.getTime() - deslocamento).toISOString().slice(0, 10);
};

const TecnicoResponsavelPage = () => {
  const { filialLogin } = useFilial();
  const tecnicoApi = useTecnicoResponsavel();
  const ordemApi = useOrdemServico();
  const navigate = useNavigate();
  const hoje = new Date();
  const [tecnicoSelecionado, setTecnicoSelecionado] = useState(null);
  const [periodo, setPeriodo] = useState({
    dataInicial: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
    dataFinal: dataLocal(hoje),
  });

  useEffect(() => {
    tecnicoApi.listar();
  }, []);

  const buscarOrdens = tecnico => {
    const selecionado = tecnico || tecnicoSelecionado;
    if (!selecionado?.id || !filialLogin?.id) return;
    ordemApi.listar({
      filialId: filialLogin.id,
      tecnicoId: selecionado.id,
      dataInicial: periodo.dataInicial || undefined,
      dataFinal: periodo.dataFinal || undefined,
    });
  };

  const selecionarTecnico = tecnico => {
    setTecnicoSelecionado(tecnico);
    buscarOrdens(tecnico);
  };

  const adicionarTecnico = async () => {
    const salvo = await tecnicoApi.showTecnicoResponsavelCad();
    if (salvo?.id) selecionarTecnico(salvo);
  };

  const editarTecnico = async tecnico => {
    const salvo = await tecnicoApi.showTecnicoResponsavelCad(tecnico);
    if (salvo?.excluido) {
      if (tecnicoSelecionado?.id === salvo.id) setTecnicoSelecionado(null);
      return;
    }
    if (salvo?.id && tecnicoSelecionado?.id === salvo.id) setTecnicoSelecionado(salvo);
  };

  return (
    <div className="servicos-page container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h3>Técnicos responsáveis</h3>
        <button type="button" className="btn btn-primary" onClick={adicionarTecnico}>
          Adicionar técnico
        </button>
      </div>

      <div className="row">
        <div className="col-lg-4">
          <section className="servicos-card">
            <h5>Técnicos cadastrados</h5>
            <div className="table-responsive tecnico-lista-scroll">
              <table className="table table-hover align-middle">
                <thead><tr><th>Nome</th><th /></tr></thead>
                <tbody>
                  {tecnicoApi.tecnicos.map(tecnico => (
                    <tr
                      key={tecnico.id}
                      className={tecnicoSelecionado?.id === tecnico.id ? 'table-primary' : ''}
                      onDoubleClick={() => selecionarTecnico(tecnico)}
                    >
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-start"
                          onClick={() => selecionarTecnico(tecnico)}
                        >
                          {tecnico.nome}
                        </button>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => editarTecnico(tecnico)}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!tecnicoApi.loading && !tecnicoApi.tecnicos.length && (
                <p className="text-center text-muted">Nenhum técnico cadastrado.</p>
              )}
            </div>
          </section>
        </div>

        <div className="col-lg-8">
          <section className="servicos-card">
            <h5>
              Ordens sob responsabilidade
              {tecnicoSelecionado ? ` — ${tecnicoSelecionado.nome}` : ''}
            </h5>
            <div className="row align-items-end mb-3">
              <div className="col-sm-5 col-md-4">
                <Input
                  label="Período inicial"
                  type="date"
                  value={periodo.dataInicial}
                  size="100%"
                  onChange={evento => setPeriodo({ ...periodo, dataInicial: evento.target.value })}
                />
              </div>
              <div className="col-sm-5 col-md-4">
                <Input
                  label="Período final"
                  type="date"
                  value={periodo.dataFinal}
                  size="100%"
                  onChange={evento => setPeriodo({ ...periodo, dataFinal: evento.target.value })}
                />
              </div>
              <div className="col-sm-2 col-md-4 mb-1">
                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  disabled={!tecnicoSelecionado || ordemApi.loading}
                  onClick={() => buscarOrdens()}
                >
                  {ordemApi.loading ? 'Buscando...' : 'Filtrar'}
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr><th>OS</th><th>Abertura</th><th>Cliente</th><th>Situação</th><th>Total</th><th /></tr>
                </thead>
                <tbody>
                  {tecnicoSelecionado && ordemApi.ordens.map(ordem => (
                    <tr key={ordem.id}>
                      <td>{String(ordem.numero).padStart(6, '0')}</td>
                      <td>{dataHora(ordem.dataAbertura)}</td>
                      <td>{ordem.clienteNome}</td>
                      <td>{SITUACOES_OS[ordem.situacao]}</td>
                      <td>{moeda(ordem.valorTotal)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/ordemServico/${ordem.id}`)}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!tecnicoSelecionado && (
                <p className="text-center text-muted">Selecione um técnico para consultar as ordens.</p>
              )}
              {tecnicoSelecionado && !ordemApi.loading && !ordemApi.ordens.length && (
                <p className="text-center text-muted">Nenhuma ordem encontrada no período.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TecnicoResponsavelPage;
